/**
 * AIAssistantBubble — a small floating chat bubble, present on every console
 * screen once a staff member is logged in (mounted once from Layout.js).
 * Minimized by default; opens into a compact chat panel on click. Always
 * talks to purpose="staff_assistant", which the backend routes through the
 * safety guard (tenant-scoped stock/price/supplier/branch lookups, role
 * gate on clinical questions, hard no-go on cost/margin/customer details —
 * see services/staff_assistant_guard.py).
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { ChatMarkdown } from './ChatMarkdown';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, Send, X, Volume2, VolumeX, Square, MessageSquarePlus, EyeOff, Boxes, Tag, Truck, ClipboardList, Pill } from 'lucide-react';
import { ExpiryQuickActions } from './duti/ExpiryQuickActions';
import { useSpokenReplies } from './duti/useSpokenReplies';
import { BubbleActionCard, ReorderDraftButtons, DutiAttach } from './duti/BubbleExtras';
import { VoiceButton } from './duti/VoiceButton';
import { ClinicalSavePanel } from './duti/ClinicalSavePanel';
import { useBubblePosition } from './duti/useBubblePosition';
import { ChatRetryNotice, chatFailure, chatHttpError } from './duti/ChatRetryNotice';

import { authFetch } from '../lib/api';
import { DUTI_AI_AVATAR } from '../lib/brand';
const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AIAssistantBubble = ({ onHide }) => {
  const { user } = useAuth();
  const bubble = useBubblePosition(user?.user_id);
  const canDraftPO = (user?.permissions || []).includes('purchases_manage');
  const isClinicalRole = ['super_admin', 'admin', 'pharmacist'].includes(user?.role);
  const firstName = (user?.name || '').trim().split(/\s+/)[0] || '';
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const speech = useSpokenReplies();
  const [heardTwi, setHeardTwi] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [contextSubject, setContextSubject] = useState('');
  const [failure, setFailure] = useState(null);
  const sessionId = useRef(null);
  const requestAbort = useRef(null);
  useEffect(() => () => requestAbort.current?.abort(), []);
  const bottomRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [open, messages]);

  const sendMessage = async (spoken) => {
    const text = (typeof spoken === 'string' ? spoken : input).trim();
    if (!text || streaming) return;
    speech.prime();
    setHeardTwi('');
    const dropFailed = !!failure;
    setFailure(null);

    setMessages((prev) => [...(dropFailed ? prev.slice(0, -1) : prev), { role: 'user', content: text }, { role: 'assistant', content: '' }]);
    setInput('');
    setStreaming(true);
    const controller = new AbortController();
    requestAbort.current = controller;

    try {
      const res = await authFetch(`${API_URL}/api/claude/chat`, {
        method: 'POST',
        signal: controller.signal,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message: text, session_id: sessionId.current, model: 'sonnet', purpose: 'staff_assistant' }),
      });
      if (!res.ok) throw await chatHttpError(res);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let full = '';
      let streamError = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const evt = JSON.parse(line.slice(6));
            if (evt.type === 'session' && evt.session_id) {
              sessionId.current = evt.session_id;
            } else if (evt.type === 'delta') {
              full += evt.content;
              setMessages((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last?.role === 'assistant') updated[updated.length - 1] = { ...last, content: last.content + evt.content };
                return updated;
              });
            } else if (evt.type === 'reorder_candidates') {
              setMessages((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last?.role === 'assistant') updated[updated.length - 1] = { ...last, reorderCandidates: evt.candidates };
                return updated;
              });
            } else if (evt.type === 'expiry_batches') {
              setMessages((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last?.role === 'assistant') updated[updated.length - 1] = { ...last, expiryBatches: evt.batches };
                return updated;
              });
            } else if (evt.type === 'chat_context') {
              setContextSubject(evt.subject || '');
            } else if (evt.type === 'duti_action') {
              setMessages((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last?.role === 'assistant') updated[updated.length - 1] = { ...last, dutiAction: evt.action };
                return updated;
              });
            } else if (evt.type === 'clinical_context') {
              setMessages((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last?.role === 'assistant') updated[updated.length - 1] = { ...last, clinicalContext: evt.context };
                return updated;
              });
            } else if (evt.type === 'error') {
              streamError = evt.message || 'Duti AI couldn\'t answer just now.';
            }
          } catch { /* skip malformed lines */ }
        }
      }
      if (streamError && !full) throw Object.assign(new Error(streamError), { status: 500 });
      if (!controller.signal.aborted) speech.autoPlay(full, messages.length + 1);
    } catch (e) {
      if (controller.signal.aborted) return;
      setFailure(chatFailure(e, text));
      setMessages((prev) => prev.slice(0, -1));
    } finally {
      setStreaming(false);
    }
  };

  const onAttachResult = (r) => {
    setMessages((prev) => [...prev, r.ok
      ? { role: 'assistant', content: `I read the ${r.label} and drafted this for approval — nothing has changed yet.`, dutiAction: r.action }
      : { role: 'assistant', content: r.message }]);
  };

  const startNewConversation = () => {
    if (streaming) return;
    speech.stop();
    sessionId.current = null;
    setMessages([]);
    setInput('');
    setHeardTwi('');
    setContextSubject('');
    setFailure(null);
  };

  const retry = (text) => sendMessage(text);

  const suggestedQuestions = [
    { icon: Boxes, text: "What's low in stock right now?" },
    { icon: Tag, text: "What's the price of paracetamol?" },
    { icon: Truck, text: 'Which supplier do we order from for amoxicillin?' },
    ...(canDraftPO ? [{ icon: ClipboardList, text: 'Draft a purchase order for low stock items' }] : []),
    ...(isClinicalRole ? [{ icon: Pill, text: 'Check interactions for two drugs' }] : []),
  ];

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  return (
    <>
      {open && (
        <div
          className="fixed z-40 flex flex-col bg-background border rounded-lg shadow-2xl overflow-hidden"
          style={bubble.panelStyle}
          data-testid="ai-bubble-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b bg-primary/5 shrink-0">
            <div className="flex items-center gap-2">
              <img src={DUTI_AI_AVATAR} alt="" aria-hidden="true" className="h-6 w-6 rounded-full" data-testid="ai-bubble-avatar" />
              <span className="font-medium text-sm" data-testid="ai-bubble-title">Duti AI</span>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { speech.stop(); onHide?.(); }} title="Hide Duti-AI" aria-label="Hide Duti-AI" data-testid="ai-bubble-hide-button">
                <EyeOff className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={startNewConversation}
                aria-label="Start new conversation" title="Start new conversation — clears what we were talking about"
                data-testid="ai-bubble-new-chat-btn">
                <MessageSquarePlus className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" className="h-7 px-2 text-[11px] font-semibold tracking-wide" onClick={speech.switchLanguage}
                title={speech.language === 'tw' ? 'Voice language: Asante Twi' : 'Voice language: English'}
                aria-label="Switch voice language" data-testid="ai-bubble-voice-lang" data-lang={speech.language}>
                {speech.language === 'tw' ? 'TWI' : 'EN'}
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={speech.toggle}
                aria-label={speech.enabled ? 'Stop reading answers aloud' : 'Read answers aloud'}
                title={speech.enabled ? 'Answers are read aloud' : 'Read answers aloud'}
                data-testid="ai-bubble-speak-toggle" data-state={speech.enabled ? 'on' : 'off'}>
                {speech.enabled ? <Volume2 className="h-4 w-4 text-primary" /> : <VolumeX className="h-4 w-4" />}
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { speech.stop(); setOpen(false); }} aria-label="Close Duti-AI panel" data-testid="ai-bubble-close-btn">
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-3" data-testid="ai-bubble-messages">
            {messages.length === 0 && (
              <div className="space-y-3" data-testid="ai-bubble-greeting">
                <div className="flex items-start gap-3">
                  <img src={DUTI_AI_AVATAR} alt="" aria-hidden="true" className="h-12 w-12 rounded-full shrink-0" />
                  <p className="text-sm pt-1">
                    <span className="font-medium">Hi{firstName ? ` ${firstName}` : ''} — I'm Duti.</span>
                    <span className="block text-muted-foreground">How can I help at the counter today?</span>
                  </p>
                </div>
                <p className="text-sm text-muted-foreground">
                  Ask about stock, pricing, suppliers or branches. Pharmacists and owners can also ask clinical questions and save answers to patient records.
                  Once your owner switches them on, I can also draft products, edits, purchase orders, invoice checks and pending sales for approval.
                </p>
                <div className="flex flex-wrap gap-1.5 pt-1" data-testid="ai-bubble-suggested-questions">
                  {suggestedQuestions.map(({ icon: Icon, text }, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => sendMessage(text)}
                      disabled={streaming}
                      className="inline-flex items-center gap-1.5 rounded-full border bg-muted/60 px-2.5 py-1 text-[11px] text-foreground hover:bg-muted hover:border-primary/40 transition-colors disabled:opacity-50"
                      data-testid={`ai-bubble-suggestion-${i}`}
                    >
                      <Icon className="h-3 w-3 text-primary shrink-0" />
                      {text}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`ai-bubble-message-${m.role}`}>
                <div
                  className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                    m.role === 'user' ? 'bg-primary text-primary-foreground whitespace-pre-wrap' : 'bg-muted'
                  }`}
                >
                  {m.role === 'assistant' && m.content ? (
                    <>
                      <ChatMarkdown content={m.content} />
                      {!(streaming && i === messages.length - 1) && (
                        <button type="button" onClick={() => (speech.playing === i ? speech.stop() : speech.play(m.content, i))}
                          className="mt-1 inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
                          data-testid={`ai-bubble-play-reply-${i}`}>
                          {speech.playing === i ? <Square className="h-3 w-3" /> : <Volume2 className="h-3 w-3" />}
                          {speech.playing === i ? 'Stop' : speech.language === 'tw' ? 'Tie wɔ Twi' : 'Listen'}
                        </button>
                      )}
                    </>
                  ) : (
                    m.content || (streaming && i === messages.length - 1 ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : '')
                  )}
                  {m.dutiAction && <BubbleActionCard action={m.dutiAction} />}
                  {m.expiryBatches?.length > 0 && <ExpiryQuickActions batches={m.expiryBatches} />}
                  {isClinicalRole && m.clinicalContext && (
                    <ClinicalSavePanel
                      question={messages[i - 1]?.content || ''}
                      answer={m.content}
                      drugs={m.clinicalContext.drugs || []}
                    />
                  )}
                  {canDraftPO && m.reorderCandidates?.length > 0 && (
                    <ReorderDraftButtons
                      candidates={m.reorderCandidates}
                      onDrafted={(a) => setMessages((prev) => [...prev, { role: 'assistant', content: 'Drafted and sent for approval — nothing has changed yet.', dutiAction: a }])}
                    />
                  )}
                </div>
              </div>
            ))}
            {failure && !streaming && (
              <ChatRetryNotice key={`${failure.text}-${failure.message}`} failure={failure} onRetry={retry} testId="ai-bubble-retry" />
            )}
            <div ref={bottomRef} />
          </div>

          {contextSubject && (
            <div className="px-3 pt-2 flex items-center gap-1.5 text-[11px] text-muted-foreground" data-testid="ai-bubble-context-chip">
              <span className="inline-flex items-center gap-1 rounded-full border bg-muted/60 px-2 py-0.5">
                Continuing about <span className="font-medium text-foreground">{contextSubject}</span>
                <button type="button" onClick={startNewConversation} className="hover:text-foreground" aria-label="Clear conversation context"
                  data-testid="ai-bubble-context-clear">
                  <X className="h-3 w-3" />
                </button>
              </span>
            </div>
          )}
          {heardTwi && (
            <div className="px-3 pt-2 text-[11px] text-muted-foreground border-t" data-testid="ai-bubble-heard-twi">
              <span className="font-medium text-foreground">Heard in Twi:</span> {heardTwi}
              <span className="block">Translated to English below — check it before sending.</span>
            </div>
          )}
          <div className={`flex items-end gap-2 p-3 shrink-0 ${heardTwi || contextSubject ? '' : 'border-t'}`}>
            <DutiAttach disabled={streaming} onResult={onAttachResult} />
            <VoiceButton disabled={streaming} language={speech.language}
              onText={(t, original) => { setInput((prev) => (prev ? `${prev} ${t}` : t)); setHeardTwi(original || ''); }} />
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask Duti AI…"
              rows={1}
              className="flex-1 min-w-0 resize-none rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary max-h-24"
              data-testid="ai-bubble-input"
            />
            <Button size="icon" className="h-9 w-9 shrink-0" onClick={sendMessage} disabled={streaming || !input.trim()} data-testid="ai-bubble-send-btn">
              {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      )}

      <div className="fixed z-40 w-16 h-16" style={{ left: bubble.point.x, top: bubble.point.y }} data-testid="ai-bubble-position">
      <Button
        {...bubble.handlers}
        onClick={() => { if (bubble.allowClick()) { speech.stop(); setOpen((v) => !v); } }}
        className={`absolute bottom-0 left-0 h-14 w-14 rounded-full shadow-xl p-0 touch-none select-none ${bubble.dragging ? 'cursor-grabbing' : 'cursor-grab'}`}
        title="Duti-AI — drag or use arrow keys to move"
        aria-label={open ? 'Close Duti AI' : 'Open Duti AI'}
        data-testid="ai-bubble-toggle-btn"
      >
        {open ? <X className="h-5 w-5" /> : <img src={DUTI_AI_AVATAR} alt="" aria-hidden="true" className="h-14 w-14 rounded-full object-cover" data-testid="ai-bubble-toggle-avatar" />}
      </Button>
      <Button variant="outline" size="icon" className="absolute right-0 top-0 h-6 w-6 rounded-full bg-background shadow-sm" onClick={() => { speech.stop(); onHide?.(); }} aria-label="Hide Duti-AI" title="Hide Duti-AI" data-testid="ai-bubble-dismiss-button">
        <X className="h-3 w-3" />
      </Button>
      </div>
    </>
  );
};
